import { Injectable } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { EsitoControllo } from '@situazioniDaControllare/cassetti-fiscali-da-controllare-models';
import { StatoSituazioneDebitoria } from 'app/main/forfettario-folder/agenzia-entrate-riscossione/agenzia-entrate-riscossione.models';
import { CreditoConSaldo, TipoSezioneF24 } from 'app/main/forfettario-folder/crediti-folder/crediti.models';
import { CreditiService } from 'app/main/forfettario-folder/crediti-folder/crediti.service';
import { ContatoriCriticitaUtente, Forfettario, ForfettarioListItem } from 'app/main/forfettario-folder/forfettario.models';
import { ForfettarioService } from 'app/main/forfettario-folder/forfettario.service';
import { AuthenticationService } from 'app/modules-services/auth-services/authentication.service';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { defaultIfEmpty, filter, switchMap, tap } from 'rxjs/operators';
import { ForfettarioDataService } from './forfettario-data.service';

@Injectable({ providedIn: 'root' })
export class SelezioneForfettarioService {
    private _forfettarioSelezionato = new BehaviorSubject<Forfettario>(null);
    private _forfettarioCorrente = new BehaviorSubject<ForfettarioListItem>(null);
    private _forfettarioCambiato = new BehaviorSubject<boolean>(null);

    constructor(
        private forfettarioService: ForfettarioService,
        private creditiService: CreditiService,
        private authenticationService: AuthenticationService,
        private forfettarioDataService: ForfettarioDataService,
        private fuseNavigationService: FuseNavigationService
    ) {
        this.forfettarioDataService.upsertEvent$
            .pipe(filter(f => this.forfettarioSelezionato && f.id === this.forfettarioSelezionato.id))
            .subscribe(forfettarioAggiornato => {
                // console.warn('SelezioneForfettarioService.upsertEvent$', forfettarioAggiornato);
                this.setForfettario(forfettarioAggiornato);
            });

        this.forfettarioSelezionato$;
    }

    get forfettarioSelezionato$() {
        return this._forfettarioSelezionato.asObservable().pipe(filter(forfettario => !!forfettario));
    }

    get forfettarioSelezionato() {
        return this._forfettarioSelezionato.getValue();
    }

    get forfettarioCorrente() {
        return this._forfettarioCorrente.getValue();
    }

    get forfettarioCorrente$() {
        return this._forfettarioCorrente.asObservable();
    }

    get forfettarioCambiato$() {
        return this._forfettarioCambiato.asObservable().pipe(filter(x => x === true));
    }

    setForfettario(forfettario: Forfettario) {
        this._forfettarioSelezionato.next(forfettario);
    }

    updateForfettarioCorrente(forfettarioId: number) {
        return this.forfettarioService
            .getFilteredForfettari(forfettarioId.toString())
            .pipe(switchMap(forfettario => this.setForfettarioCorrente(forfettario[0])));
    }

    setForfettarioCorrente(forfettario: ForfettarioListItem) {
        if (this.forfettarioCorrente && forfettario.id !== this.forfettarioCorrente.id) {
            this._forfettarioCambiato.next(true);
        } else {
            this._forfettarioCambiato.next(false);
        }

        sessionStorage.setItem('selected-forfettario', JSON.stringify(forfettario));
        this._forfettarioCorrente.next(forfettario);

        if (!this.authenticationService.authData.accessToken) return;

        const getForfettarioPerId$ = this.forfettarioService.getForfettarioPerId(forfettario.id).pipe(tap(forfettario => this.setForfettario(forfettario)));

        const loadBaseInformationForfettarioCorrente$ = this.loadBaseInformationForfettarioCorrente(forfettario.id);

        const loadCriticitaForfettario$ = this.forfettarioService
            .getContatoriCriticitaForfettario(forfettario.codiceFiscale)
            .pipe(tap(criticita => this.updateCriticitaNavigationItem(criticita)));

        const getStatoSituazioneDebitoria$ = this.creditiService.getStatoSituazioneDebitoria(forfettario.codiceFiscale).pipe(
            filter(statoSituazioneDebitoria => !!statoSituazioneDebitoria),
            switchMap(statoSituazioneDebitoria =>
                this.creditiService
                    .getCreditiConSaldo(forfettario.codiceFiscale)
                    .pipe(tap(creditiConSaldo => this.updateCreditiNavigationItem(statoSituazioneDebitoria, creditiConSaldo)))
            ),
            defaultIfEmpty(null)
        );

        const loadForfettarioLogo$ = this.forfettarioService.getLogo(forfettario.id);

        return forkJoin({
            forfettarioCorrente: getForfettarioPerId$,
            forfettarioBaseInformation: loadBaseInformationForfettarioCorrente$,
            forfettarioCriticita: loadCriticitaForfettario$,
            statoSituazioneDebitoria: getStatoSituazioneDebitoria$,
            forfettarioLogo: loadForfettarioLogo$,
        });
    }

    loadBaseInformationForfettarioCorrente(forfettarioId: number) {
        const getTokenContainerUtente$ = this.forfettarioService.getTokenContainerUtente(forfettarioId, { cacheData: true });
        const getAccoountData$ = this.forfettarioService.getAccountData(forfettarioId, { cacheData: true });

        return forkJoin({ containerSasUri: getTokenContainerUtente$, accountData: getAccoountData$ });
    }

    /**
     * Sappiamo dell'esistenza del metodo updateNavigationItem() ma per qualche motivo non chiaro la UI non viene aggiornata pur essendo
     * aggiornato l'array della navigazione. Si è deciso di attuare questo workaround
     */
    updateCriticitaNavigationItem(criticita: Array<ContatoriCriticitaUtente>) {
        const criticitaInErrore = criticita.find(x => x.esito.id === EsitoControllo.Errore);

        this.fuseNavigationService.removeNavigationItem('criticita-utente');
        this.fuseNavigationService.addNavigationItem(
            {
                id: 'criticita-utente',
                title: 'Criticità',
                type: 'item',
                icon: 'label',
                badge: criticitaInErrore?.numero > 0 ? { title: criticitaInErrore.numero.toString(), bg: 'red' } : null,
                url: '/forfettario/criticita',
            },
            'forfettario'
        );
    }

    updateCreditiNavigationItem(statoSituazioneDebitoria: StatoSituazioneDebitoria, creditiConSaldo: CreditoConSaldo[]) {
        this.fuseNavigationService.updateNavigationItem('crediti', {
            id: 'crediti',
            title: 'Crediti',
            type: 'item',
            icon: 'label',
            badge: statoSituazioneDebitoria?.hasDebitoErarialeEccessivo
                ? {
                      title: creditiConSaldo.filter(x => x.sezioneTributo === TipoSezioneF24.Erario || x.sezioneTributo === TipoSezioneF24.Regioni).length,
                      bg: 'red',
                  }
                : null,
            url: '/forfettario/crediti',
        });
    }
}
